import React from 'react';
import './IrisMascot.css';

/**
 * IRIS - The Eye Fitness Mascot
 *
 * A friendly owl with expressive, colorful iris eyes.
 * Used on the landing page, dashboard, and completion screens.
 *
 * Props:
 *   size   - 'small' (64px) | 'medium' (100px) | 'large' (160px)
 *   mood   - 'happy' | 'proud' | 'excited' | 'sleepy' | 'neutral'
 *   message - Optional speech bubble text
 */

const SIZES = {
    small: 64,
    medium: 100,
    large: 160,
};

function IrisMascot({ size = 'medium', mood = 'happy', message }) {
    const px = SIZES[size] || SIZES.medium;
    const isSleepy = mood === 'sleepy';
    const isExcited = mood === 'excited';

    return (
        <div className={`iris-mascot iris-mascot--${size} ${isExcited ? 'iris-mascot--excited' : ''}`}>
            <div className="iris-mascot__figure" style={{ width: px, height: px }}>
                <svg viewBox="0 0 200 220" className="iris-mascot__svg">
                    {/* Ear tufts */}
                    <polygon points="62,48 46,16 74,40" fill="var(--avatar-primary)" stroke="var(--avatar-outline)" strokeWidth="1.5" />
                    <polygon points="138,48 154,16 126,40" fill="var(--avatar-primary)" stroke="var(--avatar-outline)" strokeWidth="1.5" />
                    <polygon points="62,48 50,24 70,42" fill="var(--avatar-secondary)" />
                    <polygon points="138,48 150,24 130,42" fill="var(--avatar-secondary)" />

                    {/* Body */}
                    <ellipse cx="100" cy="165" rx="52" ry="38" fill="var(--avatar-primary)" stroke="var(--avatar-outline)" strokeWidth="2" />
                    {/* Belly pattern */}
                    <ellipse cx="100" cy="170" rx="32" ry="26" fill="var(--avatar-secondary)" opacity="0.45" />
                    {/* Chest feather V-shapes */}
                    <path d="M92 155 L100 162 L108 155" fill="none" stroke="var(--avatar-inner)" strokeWidth="1.2" opacity="0.6" />
                    <path d="M88 165 L100 173 L112 165" fill="none" stroke="var(--avatar-inner)" strokeWidth="1.2" opacity="0.5" />
                    <path d="M90 175 L100 182 L110 175" fill="none" stroke="var(--avatar-inner)" strokeWidth="1.2" opacity="0.4" />

                    {/* Wings */}
                    <ellipse cx="46" cy="148" rx="18" ry="34" fill="var(--avatar-primary)" stroke="var(--avatar-outline)" strokeWidth="1.5" transform="rotate(-8 46 148)" />
                    <ellipse cx="154" cy="148" rx="18" ry="34" fill="var(--avatar-primary)" stroke="var(--avatar-outline)" strokeWidth="1.5" transform="rotate(8 154 148)" />

                    {/* Head */}
                    <ellipse cx="100" cy="85" rx="58" ry="48" fill="var(--avatar-primary)" stroke="var(--avatar-outline)" strokeWidth="2" />
                    {/* Facial disc */}
                    <ellipse cx="100" cy="88" rx="48" ry="40" fill="var(--avatar-secondary)" opacity="0.3" />

                    {/* Eye rings */}
                    <circle cx="74" cy="82" r="24" fill="var(--avatar-eye-ring)" stroke="var(--avatar-outline)" strokeWidth="1.5" />
                    <circle cx="126" cy="82" r="24" fill="var(--avatar-eye-ring)" stroke="var(--avatar-outline)" strokeWidth="1.5" />

                    {/* Eyes */}
                    <g className={`iris-mascot__eyes ${isSleepy ? 'iris-mascot__eyes--sleepy' : ''}`}>
                        {/* Left eye */}
                        <circle cx="74" cy="82" r={isSleepy ? 6 : 19} fill="var(--avatar-eye-white)" stroke="var(--avatar-outline)" strokeWidth="1" style={{ transition: 'r 0.4s ease' }} />
                        {!isSleepy && (
                            <>
                                {/* Left iris - colorful! */}
                                <circle cx="74" cy="82" r="13" className="iris-mascot__iris" />
                                <circle cx="74" cy="82" r="7" fill="var(--avatar-pupil)" />
                                {/* Sparkle */}
                                <circle cx="69" cy="77" r="3.5" fill="var(--avatar-eye-shine)" />
                                <circle cx="79" cy="85" r="1.5" fill="var(--avatar-eye-shine)" opacity="0.6" />
                            </>
                        )}

                        {/* Right eye */}
                        <circle cx="126" cy="82" r={isSleepy ? 6 : 19} fill="var(--avatar-eye-white)" stroke="var(--avatar-outline)" strokeWidth="1" style={{ transition: 'r 0.4s ease' }} />
                        {!isSleepy && (
                            <>
                                {/* Right iris - colorful! */}
                                <circle cx="126" cy="82" r="13" className="iris-mascot__iris" />
                                <circle cx="126" cy="82" r="7" fill="var(--avatar-pupil)" />
                                {/* Sparkle */}
                                <circle cx="121" cy="77" r="3.5" fill="var(--avatar-eye-shine)" />
                                <circle cx="131" cy="85" r="1.5" fill="var(--avatar-eye-shine)" opacity="0.6" />
                            </>
                        )}
                    </g>

                    {/* Sleepy Z's */}
                    {isSleepy && (
                        <g className="iris-mascot__zzz">
                            <text x="148" y="60" fontSize="16" fill="var(--text-muted)" fontWeight="700">z</text>
                            <text x="160" y="44" fontSize="12" fill="var(--text-muted)" fontWeight="700" opacity="0.6">z</text>
                            <text x="168" y="32" fontSize="9" fill="var(--text-muted)" fontWeight="700" opacity="0.3">z</text>
                        </g>
                    )}

                    {/* Beak */}
                    <polygon points="100,98 93,110 107,110" fill="var(--avatar-beak)" stroke="var(--avatar-outline)" strokeWidth="1.5" strokeLinejoin="round" />

                    {/* Smile (when happy/proud/excited) */}
                    {(mood === 'happy' || mood === 'proud' || mood === 'excited') && (
                        <path d="M92 114 Q100 120 108 114" fill="none" stroke="var(--avatar-outline)" strokeWidth="1.2" strokeLinecap="round" />
                    )}

                    {/* Feet */}
                    <g fill="var(--avatar-beak)" stroke="var(--avatar-outline)" strokeWidth="1">
                        <path d="M80 198 L75 208 M80 198 L80 209 M80 198 L85 208" strokeLinecap="round" />
                        <path d="M120 198 L115 208 M120 198 L120 209 M120 198 L125 208" strokeLinecap="round" />
                    </g>
                </svg>
            </div>

            {message && (
                <div className="iris-mascot__bubble">
                    <span>{message}</span>
                </div>
            )}
        </div>
    );
}

export default IrisMascot;
